import React from 'react';
import ReactDOM from 'react-dom/client';
import './assets/main.css'
import App from './App';
import * as serviceWorker from './serviceWorker';
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { msalInstance, loginRequest, tokenRequest } from './auth/authProvider';

import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import PageSpinner from './components/global/pageSpinner';
import LoginError from './components/global/LoginError';
import PublicAccessDeniedRoute from './components/global/PublicAccessDeniedRoute';
import { AuthContextProvider } from "./context/authContext";
import { registerLicense } from '@syncfusion/ej2-base';

const {REACT_APP_API_URL} = process.env;

// Production still uses Syncfusion file-upload controls. Keep their licence
// registration until those controls have also been replaced in this environment.
registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE_KEY);

const httpLink = createHttpLink({
  uri: REACT_APP_API_URL
});

export const authFlow = (success, fail) => {
  const accounts = msalInstance.getAllAccounts();
  return msalInstance.acquireTokenSilent({
    ...tokenRequest,
    account: accounts[0],
  })
    .then(tokenResponse => {
      success({
        headers: {
          authorization: tokenResponse ? `Bearer ${tokenResponse.accessToken}` : "",
        }
      });
    })
    .catch(error => {
      console.log("acquireTokenSilent>>>>", error);
      // Skip interactive fallback if no accounts exist — MsalAuthenticationTemplate
      // handles re-authentication. Calling acquireTokenRedirect here causes
      // interaction_in_progress when multiple Apollo requests fire concurrently.
      if (accounts.length === 0) {
        fail(error);
        return;
      }
      return msalInstance.acquireTokenPopup({
        ...tokenRequest,
        account: accounts[0],
      }).then(tokenResponse => {
        success({
          headers: {
            authorization: tokenResponse ? `Bearer ${tokenResponse.accessToken}` : "",
          }
        });
      }).catch(popupError => {
        console.log("acquireTokenPopup>>>>", popupError);
        fail(popupError);
      });
    })
}

let loginRedirectInProgress = false;

const errorLink = onError(({ networkError, graphQLErrors }) => {
  console.log('networkError', networkError);
  console.log('graphQLError', graphQLErrors);
  if (networkError && (networkError.statusCode === 400)) {
    if (graphQLErrors && graphQLErrors[0].message === 'Context creation failed: Not Authorized') {
      if (!loginRedirectInProgress) {
        loginRedirectInProgress = true;
        msalInstance.loginRedirect(loginRequest).catch(e => {
          console.log("errorLink loginRedirect error>>>>", e);
          loginRedirectInProgress = false;
        });
      }
    }
  }
})

const asyncAuthLink = setContext(() =>
  new Promise((success, fail) => authFlow(success, fail))
)

const link = ApolloLink.from([
  errorLink,
  asyncAuthLink,
  httpLink,
]);

export const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          notificationSearch: {
            merge(existing, incoming){
              return incoming
            }
          }
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* Single top-level Router: App.js's own routes (Routes.js) nest under
        this one, they no longer bring their own BrowserRouter. /access-denied
        is a sibling of the protected tree, not a descendant of
        MsalAuthenticationTemplate, so reaching it after a denial-triggered
        logout never re-triggers an interactive login (see
        PublicAccessDeniedRoute.js and docs/amends/user-access-restriction
        /tasks.md P5-8/P5-9). */}
    <Router>
      <MsalProvider instance={msalInstance}>
        <Routes>
          <Route path="/access-denied" element={<PublicAccessDeniedRoute />} />
          <Route
            path="*"
            element={
              <MsalAuthenticationTemplate
                authenticationRequest={loginRequest}
                interactionType={InteractionType.Redirect}
                errorComponent={LoginError}
                loadingComponent={PageSpinner}>
                <AuthContextProvider>
                  <ApolloProvider client={client}>
                    <App />
                  </ApolloProvider>
                </AuthContextProvider>
              </MsalAuthenticationTemplate>
            }
          />
        </Routes>
      </MsalProvider>
    </Router>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
